import { DocumentRecordStatus } from '@/__generated__/graphql';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import DatePicker from '@/components/basicComponents/datePicker';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { isValid } from 'date-fns';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_RECORD_MUTATION } from '../queries';

interface PublishUpdateProps {
  isOpen: boolean;
  publishDate: string;
  updateId: string | null;
  ownerEmail: string | null;
  refetch: () => void;
  onClose: () => void;
}

const PublishUpdate = ({ isOpen, publishDate, updateId, ownerEmail, refetch, onClose }: PublishUpdateProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, reset, getValues, handleSubmit } = useForm({ defaultValues: { publishDate: new Date().toString() } });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (publishDate && isValid(new Date(publishDate))) {
      reset({ publishDate });
    }
  }, [publishDate]);

  const [updateDocumentRecordStatus, { loading: updateDocumentRecordStatusLoading }] = useMutation(
    STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_RECORD_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        setErrorMessage(defaultErrorMessage);
      }
    }
  );

  const saveValue = () => {
    setErrorMessage(null);

    if (!updateId || !ownerEmail) {
      setErrorMessage(defaultErrorMessage);
      return;
    }

    const { publishDate } = getValues();

    updateDocumentRecordStatus({
      variables: {
        data: {
          id: updateId,
          status: DocumentRecordStatus.Published,
          statusUpdatedAt: publishDate,
          publisherEmail: ownerEmail
        }
      },
      onCompleted: () => {
        refetch();
        onClose();
      }
    });
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Publish Update"
      confirmButton={{
        name: 'Publish',
        onClick: handleSubmit(saveValue),
        disabled: updateDocumentRecordStatusLoading
      }}
      isUseOutsideClick={false}
      showRequiredFields={!updateDocumentRecordStatusLoading}
      showCloseIcon={!updateDocumentRecordStatusLoading}
      showCancelButton={!updateDocumentRecordStatusLoading}
    >
      {updateDocumentRecordStatusLoading ? (
        <ModalLoader />
      ) : (
        <ModalBody>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <Controller
            name="publishDate"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Publish Date is required'
              }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <DatePicker
                label="Publish Date"
                value={value || ''}
                onChange={(date) => {
                  onChange(date ? new Date(date) : '');
                }}
                errorText={error?.message}
                isRequiredField
              />
            )}
          />
          <Banner
            title=""
            description="Flag this commitment to highlight it in investor portfolios."
            icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
            bgColor={themeVar('layer.1')}
            color={themeVar('font.strong')}
          />
        </ModalBody>
      )}
    </ModalWrappedContent>
  );
};

export default PublishUpdate;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
